import { type VariantProps, cva } from "class-variance-authority";
import { ComponentProps } from "react";

import { cn } from "@/lib/cn";

// DESIGN.md → Status chips and tags: a tag says what property something has, in the Badge style and
// sentence case, on Fog with Graphite text
export const tagVariants = cva(
  "inline-flex h-5.5 items-center gap-1 whitespace-nowrap rounded-sm px-2 text-xs font-semibold",
  {
    variants: {
      tone: {
        neutral: "bg-muted text-neutral-fg",
        // Only for a rare property that calls for care; one most rows carry stays neutral
        care: "bg-warning-subtle text-warning",
        // Rarer still: a property that puts the whole company in the holder's hands
        danger: "bg-destructive-subtle text-destructive-fg",
        // A tag that must stay apart from a Fog hover or a Lilac Wash selected row, where the Fog
        // fill disappears: white with a hairline ring, which takes no room. It sits in a list row
        // whose name is the primary text, so it reads a step quieter: medium weight in Slate Grey,
        // leaving full ink to what the search marked
        outline: "bg-background font-medium text-muted-foreground ring-1 ring-inset ring-border",
        // A tag that links to the filtered view it names
        linked: "bg-primary-subtle text-primary-fg underline-offset-2 hover:underline",
      },
      // A long text wraps in a narrow column instead of widening it: between words first, and
      // inside a word with no break (a permission key) only where it would overflow. One 16px line
      // with 3px above and below is the tag's 22px; a second line keeps the same 3px around it
      wrap: {
        true: "h-auto min-h-5.5 whitespace-normal py-0.75 leading-4 wrap-anywhere",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

type TagProps = ComponentProps<"span"> & VariantProps<typeof tagVariants>;

/** A read-only property label. Spell its meaning out in a `title` when the text is short. */
export const Tag = ({ tone, wrap, className, ...props }: TagProps) => (
  <span className={cn(tagVariants({ tone, wrap }), className)} {...props} />
);
